
import { Team, User } from '@/classes/auth';
import { Invoice, LoadingState, PaymentMethod, Plan, Subscription } from '@/classes/stripe';
import StripeSubscriptionService from '@/Services/Subscription/StripeSubscriptionService';
import store from '@/store';
import * as Sentry from '@sentry/vue';
import axios, { AxiosError } from 'axios';
import moment from 'moment';

import AppConfig from '@/AppConfig';
import Icon from '@/components/Icons/Icon.vue';
import BillingAddressEdit from '@/components/Modals/Account/BillingAddressEdit.vue';
import CancelPauseSubscription from '@/components/Modals/Account/CancelPauseSubscription.vue';
import InvoiceEmailEdit from '@/components/Modals/Account/InvoiceEmailEdit.vue';
import PaymentMethodEdit from '@/components/Modals/Account/PaymentMethodEdit.vue';
import ReactivateSubscription from '@/components/Modals/Account/ReactivateSubscription.vue';
import ResumeSubscription from '@/components/Modals/Account/ResumeSubscription.vue';
import VatIdEdit from '@/components/Modals/Account/VatIdEdit.vue';
import Alert from '@/components/Alert.vue';

import { Component, Vue, Watch } from 'vue-property-decorator';
import Invoices from '@/components/Stripe/Invoices.vue';

@Component({
  components: {
    Invoices,
    BillingAddressEdit,
    VatIdEdit,
    CancelPauseSubscription,
    ReactivateSubscription,
    ResumeSubscription,
    InvoiceEmailEdit,
    PaymentMethodEdit,
    Icon,
    Alert,
  },
})
export default class activeSubscription extends Vue {
  team: Team = store.state.auth.team;
  // Modal states
  isDialogShown: any = {
    subscriptionCancelPause: false,
    addressEdit: false,
    vatIdEdit: false,
    invoiceEmailEdit: false,
    subscriptionResume: false,
    subscriptionReactivate: false,
    paymentMethodEdit: false,
  };

  isPaymentMethodsError = false;
  isPaymentMode = false;
  isPaymentMethodsLoading = false;

  legalEntityB2B: string = 'b2b';
  legalEntityB2C: string = 'b2c';

  paymentMethods: Array<PaymentMethod> = [];

  tryAgainPaymentMethod = false;
  tryAgainSubscription = false;
  errorSubscription = false;

  // Update state of update team request
  isTeamUpdating = false;

  // Handles payment method logic and some of the form state
  stripeSubscriptionService: StripeSubscriptionService = new StripeSubscriptionService();

  @Watch('store.state.auth.team', { deep: true })
  onTeamUpdate(newVal: Team): void {
    this.team = newVal;
  }

  get activeSubscription(): Subscription {
    return store.state.subscription.activeSubscription;
  }

  get activePlan(): Plan {
    return store.state.subscription.activePlan;
  }

  get expiredSubscription(): boolean {
    return this.activeSubscription.is_cancelled && this.activeSubscription.ends_at?.isBefore(moment());
  }

  get defaultPaymentMethod(): PaymentMethod | undefined {
    return this.paymentMethods.find((item: PaymentMethod) => item.is_default);
  }

  get subscriptionPlans(): Plan[] {
    return store.state.subscription.plans;
  }

  /**
   * We check here if the current active plan is still upgradeable or not
   * by comparing against other plan levels.
   */
  get upgradeButton(): boolean {
    // !== 0 is there because if a user does not have a plan the ID will still be 0 due to the class setup
    return this.activePlan && this.activePlan.id !== 0;
  }

  get getPaymentMethodTitle(): string {
    if (this.team.is_payment_method_invoice) {
      return this.$t('fields.checkout.payment_methods.send_invoice').toString();
    }
    switch (this.defaultPaymentMethod?.type) {
      case 'card':
        return this.$t('fields.checkout.payment_methods.card').toString();
      case 'sepa_debit':
        return this.$t('fields.checkout.payment_methods.sepa_debit').toString();
      default:
        return this.$t('fields.checkout.payment_methods.send_invoice').toString();
    }
  }

  get isCustomerB2BAndNeedsVatId(): boolean {
    return store.getters['location/isVatIdNeeded'] && this.team.legal_entity === 'b2b';
  }

  get paymentIconName(): string | boolean {
    if (this.team.is_payment_method_invoice) {
      return 'pay-sepa-system';
    }
    const icons: { [key: string]: string } = {
      mastercard: 'pay-mastercard-system',
      visa: 'pay-visa-system',
      amex: 'pay-amex-system',
      sepa_debit: 'pay-direct-debit-system',
    };
    if (this.defaultPaymentMethod!.brand) {
      return icons[this.defaultPaymentMethod!.brand];
    }
    return false;
  }

  get showLoadingSubscription(): LoadingState {
    return store.state.subscription.showLoading;
  }

  get isDowngrading(): boolean {
    return this.stripeSubscriptionService.isPlanDowngrading(this.activeSubscription.plan_id);
  }

  get isPaymentMethodExpired(): boolean {
    if (this.team.is_payment_method_invoice) {
      return false;
    }
    return (
      this.defaultPaymentMethod!.exp_month < moment().format('M') &&
      this.defaultPaymentMethod!.exp_year < moment().format('YYYY')
    );
  }

  get invoices(): Invoice[] {
    return store.state.account.invoices || [];
  }

  get hasInvoices(): boolean {
    return this.invoices?.length > 0 || false;
  }

  get newPlanName(): string {
    if (!this.activeSubscription.new_plan_id) return '';
    return this.stripeSubscriptionService.findPlan(this.activeSubscription.new_plan_id)?.title || '';
  }

  get user(): User {
    return store.state.auth.user;
  }

  openDialog(dialog: string): void {
    this.isDialogShown[dialog] = true;
  }

  hideDialog(name: string): void {
    this.isDialogShown[name] = false;
  }

  loadPaymentMethods(tryAgain?: boolean): Promise<any> {
    if (tryAgain) {
      this.isPaymentMethodsError = false;
      this.tryAgainPaymentMethod = true;
    } else {
      this.isPaymentMethodsLoading = true;
    }

    return axios
      .get(AppConfig.getAPIBaseUrl() + '/subscription/stripe/payment-methods/')
      .then((response) => {
        this.paymentMethods = response.data.data;
        this.isPaymentMethodsError = false;
        this.tryAgainPaymentMethod = false;
      })
      .catch((error: AxiosError) => {
        Sentry.captureException(error);
        this.isPaymentMethodsError = true;
        this.tryAgainPaymentMethod = false;
      })
      .finally(() => {
        this.isPaymentMethodsLoading = false;
      });
  }

  async loadSubscriptionPlan(tryAgain?: boolean): Promise<void> {
    if (tryAgain) {
      this.errorSubscription = false;
      this.tryAgainSubscription = true;
    }

    // Reload Subscription
    if (this.activeSubscription.id == 0 || tryAgain) {
      await this.$store.dispatch('subscription/loadSubscription');
      this.errorSubscription = false;
      this.tryAgainSubscription = false;
    }

    // Validate reloaded subscription
    if (this.activeSubscription.id == 0) {
      this.errorSubscription = true;
      this.tryAgainSubscription = false;
    }

    if (this.invoices) {
      this.errorSubscription = false;
    }
  }

  onRenewSubscription(planTileModel: Plan): void {
    this.$store.dispatch('subscription/setSubscriptionPlan', planTileModel).then(() => {
      this.$router.push({ name: 'checkout_payment_form' });
    });
  }

  lastTwoDigitsOfYear(year: string | number): number {
    return Number(year) % 100;
  }

  async created(): Promise<void> {
    await this.stripeSubscriptionService.initSubscription();
    await this.loadSubscriptionPlan();
    await this.loadPaymentMethods();
  }

  updatePaymentAddress(): void {
    this.openDialog('addressEdit');
    this.isPaymentMode = true;
  }
}
